/**
 * Authoritative census of command families handled by computeInverse.
 * Keeping this boundary data-only lets dependency checks and callers inspect
 * inverse coverage without importing the large geometry mutation module.
 */

const HEADER_INVERSE_COMMANDS = [
  "header/set-insertion-base",
  "header/set-cecolor",
] as const;

const PARAMETRIC_INVERSE_COMMANDS = [
  "geometry/move-entities",
  "geometry/rotate-entities",
  "geometry/scale-entities",
  "geometry/mirror-entities",
  "geometry/offset-entity",
  "geometry/copy",
] as const;

const VIEWPORT_INVERSE_COMMANDS = [
  "geometry/viewport-set-lock",
  "geometry/viewport-set-shadeplot",
  "geometry/viewport-vplayer",
  "geometry/viewport-clip-set",
  "geometry/viewport-clip-set-polygon",
  "geometry/viewport-clip-remove",
  "geometry/viewport-set-scale",
  "geometry/viewport-set-view",
] as const;

const CREATE_INVERSE_COMMANDS = [
  "geometry/create-revcloud",
  "geometry/create-line",
  "geometry/create-point",
  "geometry/create-circle",
  "geometry/create-arc",
  "geometry/create-ellipse",
  "geometry/create-spline",
  "geometry/create-lwpolyline",
  "geometry/create-donut",
  "geometry/create-text",
  "geometry/create-mtext",
  "geometry/create-dimension",
  "geometry/create-multileader",
  "geometry/create-wipeout",
  "geometry/create-hatch",
  "geometry/create-viewport",
  "geometry/create-arrayrect",
  "geometry/create-arraypolar",
  "geometry/create-arraypath",
  "geometry/arrayedit",
  "geometry/hatchedit",
  "geometry/create-block-insert",
  "geometry/create-block-inserts-batch",
  "geometry/create-block-definition",
] as const;

const DELETE_INVERSE_COMMANDS = [
  "geometry/delete-entity",
  "shell/delete-entities",
] as const;

// Regression fix (undo-of-property-change, post-#719): `computeInverse`'s
// pre-switch `if (asX.type === "...")` guards for `properties/apply` /
// `chprop/apply` / `matchprop/apply` (geometryMutations.ts — the Phase 4.1.e
// property-command inverses, including the Codex P2-1 default→N restore for
// Thickness/Linetype Scale) were dropped from THIS list when it was authored,
// so `hasGeometryInverseFamily` rejected every property-edit undo before it
// ever reached that code. `computeInverseCoverage.ts`'s
// `COMPUTE_INVERSE_COVERED_TYPES` — the pre-existing, independently
// maintained "side A" census for the app-level completeness test — already
// listed all three; `geometryInverseFamilies.parity.test.ts` now asserts the
// two censuses stay in exact sync so a future divergence fails loudly instead
// of silently no-opping undo.
const PROPERTIES_INVERSE_COMMANDS = [
  "properties/apply",
  "chprop/apply",
  "matchprop/apply",
] as const;

const SNAPSHOT_INVERSE_COMMANDS = [
  "geometry/trim-entity",
  "geometry/extend-entity",
  "geometry/fillet-entities",
  "geometry/chamfer-entities",
  "geometry/break-entity",
  "geometry/join-entities",
  "geometry/explode-entities",
  "geometry/explode-insert",
  "geometry/lengthen-entity",
  "geometry/stretch-entities",
] as const;

export const GEOMETRY_INVERSE_FAMILY_COMMANDS = [
  ...HEADER_INVERSE_COMMANDS,
  ...PARAMETRIC_INVERSE_COMMANDS,
  ...VIEWPORT_INVERSE_COMMANDS,
  ...CREATE_INVERSE_COMMANDS,
  ...DELETE_INVERSE_COMMANDS,
  ...SNAPSHOT_INVERSE_COMMANDS,
  ...PROPERTIES_INVERSE_COMMANDS,
] as const;

export const GEOMETRY_INVERSE_FAMILY_COMMAND_SET: ReadonlySet<string> =
  new Set(GEOMETRY_INVERSE_FAMILY_COMMANDS);

export function hasGeometryInverseFamily(commandType: string): boolean {
  return GEOMETRY_INVERSE_FAMILY_COMMAND_SET.has(commandType);
}
